import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useSipPhone } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Phone,
  Flag,
  FileText,
  Loader2,
  PhoneIncoming,
  PhoneOutgoing,
} from "lucide-react";

const STATUS_COLORS = {
  flagged: "bg-yellow-600",
  known: "bg-blue-600",
  suspicious: "bg-orange-600",
  blocked: "bg-red-600",
  vip: "bg-purple-600",
  other: "bg-slate-600",
};

function formatDateTime(isoStr) {
  if (!isoStr) return "";
  try {
    const d = new Date(isoStr);
    return d.toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "numeric",
      day: "numeric",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).replace(",", "");
  } catch {
    return isoStr;
  }
}

function formatDateOnly(isoStr) {
  if (!isoStr) return "";
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric" });
  } catch {
    return isoStr;
  }
}

function formatDuration(seconds) {
  const s = parseInt(seconds) || 0;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function PhoneLookupPopup({ phone, open, onOpenChange }) {
  const { handlePhoneClick } = useSipPhone() || {};
  const [activeTab, setActiveTab] = useState("incidents");
  const [incidents, setIncidents] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const [contactInfo, setContactInfo] = useState(null);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && phone) {
      setActiveTab("incidents");
      fetchIncidents();
      fetchCallHistory();
      fetchContact();
    }
  }, [open, phone]);

  const fetchIncidents = async () => {
    setLoadingIncidents(true);
    try {
      const res = await axios.get(`${API}/hotline/incidents/${encodeURIComponent(phone)}`, { withCredentials: true });
      const d = res.data;
      setIncidents(Array.isArray(d) ? d : d?.incidents || []);
    } catch {
      setIncidents([]);
    } finally {
      setLoadingIncidents(false);
    }
  };

  const fetchCallHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/hotline/call-history/${encodeURIComponent(phone)}`, { withCredentials: true });
      const d = res.data;
      setCallHistory(Array.isArray(d) ? d : d?.calls || []);
    } catch {
      setCallHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchContact = async () => {
    try {
      // Use backend search which has flexible phone matching
      const digits = (phone || "").replace(/[^\d]/g, "");
      const searchTerm = digits.length > 10 && digits.startsWith("1") ? digits.slice(1) : digits;
      if (!searchTerm || searchTerm.length < 4) { setContactInfo(null); return; }
      const res = await axios.get(`${API}/hotline/contacts`, { params: { search: searchTerm }, withCredentials: true });
      const contacts = Array.isArray(res.data) ? res.data : (res.data?.contacts || []);
      // Pick the best match from results
      const normalize = (p) => {
        let d = (p || "").replace(/[^\d]/g, "");
        if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
        return d;
      };
      const phoneNorm = normalize(phone);
      const match = contacts.find((c) => normalize(c.phone_number) === phoneNorm) || contacts[0] || null;
      setContactInfo(match);
    } catch {
      setContactInfo(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-slate-900 border-slate-700 z-[10000]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            <button
              className="text-blue-400 hover:underline cursor-pointer bg-transparent border-none p-0 font-inherit text-inherit"
              onClick={() => handlePhoneClick?.(phone)}
              title="Call this number"
            >
              {phone}
            </button>
          </DialogTitle>
        </DialogHeader>

        {/* Contact info */}
        {contactInfo ? (
          <div className="bg-slate-800 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2">
              {contactInfo.status === "known" ? (
                <>
                  <Phone className="h-4 w-4 text-blue-400" />
                  <span className="text-sm font-medium text-blue-400">Known Contact</span>
                </>
              ) : contactInfo.status === "flagged" ? (
                <>
                  <Flag className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm font-medium text-yellow-400">Flagged Contact</span>
                </>
              ) : (
                <>
                  <Flag className="h-4 w-4 text-slate-400" />
                  <span className="text-sm font-medium">Contact</span>
                </>
              )}
              <Badge className={`text-[9px] px-1.5 py-0 ${STATUS_COLORS[contactInfo.status] || STATUS_COLORS.other}`}>
                {contactInfo.status || "unknown"}
              </Badge>
            </div>
            {contactInfo.name && (
              <div className="text-sm">
                <span className="text-slate-400">Name:</span> {contactInfo.name}
              </div>
            )}
            {contactInfo.notes && (
              <div className="text-xs text-slate-400">{contactInfo.notes}</div>
            )}
          </div>
        ) : (
          <div className="bg-slate-800 rounded-lg p-3">
            <span className="text-sm text-slate-400">Not a known contact</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-700">
          <button
            className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "incidents"
                ? "border-blue-500 text-blue-400"
                : "border-transparent text-slate-400 hover:text-slate-300"
            }`}
            onClick={() => setActiveTab("incidents")}
          >
            <FileText className="h-3.5 w-3.5 inline mr-1" />
            Linked Incidents
            {incidents.length > 0 && (
              <span className="ml-1 text-[10px] bg-slate-700 rounded-full px-1.5">{incidents.length}</span>
            )}
          </button>
          <button
            className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "history"
                ? "border-blue-500 text-blue-400"
                : "border-transparent text-slate-400 hover:text-slate-300"
            }`}
            onClick={() => setActiveTab("history")}
          >
            <Phone className="h-3.5 w-3.5 inline mr-1" />
            Call History
            {callHistory.length > 0 && (
              <span className="ml-1 text-[10px] bg-slate-700 rounded-full px-1.5">{callHistory.length}</span>
            )}
          </button>
        </div>

        {/* Linked Incidents */}
        {activeTab === "incidents" && (
          <div>
            {loadingIncidents ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : incidents.length === 0 ? (
              <div className="text-center py-4 text-sm text-slate-500">
                No incidents linked to this number.
              </div>
            ) : (
              <div className="space-y-2">
                {incidents.map((inc) => (
                  <div
                    key={inc.incident_id}
                    className="bg-slate-800 rounded-lg p-3 hover:bg-slate-700/50 cursor-pointer transition-colors"
                    onClick={() => { onOpenChange(false); navigate(`/incidents/${inc.incident_id}`); }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-medium truncate">{inc.headline || "No headline"}</span>
                          {inc.callback_caller_type && (
                            <Badge className={`text-[9px] px-1.5 py-0 ${inc.callback_caller_type === "cv" ? "bg-cyan-700" : "bg-indigo-700"}`}>
                              {inc.callback_caller_type === "cv" ? "CV" : "3RD PARTY"}
                            </Badge>
                          )}
                          {inc.callback_has_visual && (
                            <Badge className="text-[9px] px-1.5 py-0 bg-green-700">VISUAL</Badge>
                          )}
                          {inc.has_arrest && (
                            <Badge className="text-[9px] px-1.5 py-0 bg-red-600">ARREST</Badge>
                          )}
                          {inc.has_ready && (
                            <Badge className="text-[9px] px-1.5 py-0 bg-amber-600">READY</Badge>
                          )}
                          {inc.match_types?.includes("victim") && (
                            <Badge className="text-[9px] px-1.5 py-0 bg-emerald-700">VICTIM</Badge>
                          )}
                          {inc.match_types?.includes("witness") && (
                            <Badge className="text-[9px] px-1.5 py-0 bg-sky-700">WITNESS</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-400 flex-wrap">
                          <span>{formatDateOnly(inc.date_time)}</span>
                          {inc.incident_type && <span>&bull; {inc.incident_type}</span>}
                          {inc.status && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0">{inc.status}</Badge>
                          )}
                          {inc.priority && (
                            <Badge variant="outline" className={`text-[9px] px-1 py-0 ${
                              inc.priority === "critical" ? "border-red-500 text-red-400" :
                              inc.priority === "high" ? "border-orange-500 text-orange-400" : ""
                            }`}>{inc.priority}</Badge>
                          )}
                        </div>
                        {inc.address && (
                          <div className="text-xs text-slate-500 mt-0.5 truncate">{inc.address}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Call History */}
        {activeTab === "history" && (
          <div>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : callHistory.length === 0 ? (
              <div className="text-center py-4 text-sm text-slate-500">
                No call history found.
              </div>
            ) : (
              <div className="space-y-1.5">
                {callHistory.map((call) => (
                  <div
                    key={call.call_id || call.sid}
                    className="bg-slate-800 rounded-lg px-3 py-2 flex items-center gap-3"
                  >
                    <div className="flex-shrink-0">
                      {call.direction?.startsWith("outbound") ? (
                        <PhoneOutgoing className="h-4 w-4 text-blue-400" />
                      ) : (
                        <PhoneIncoming className="h-4 w-4 text-green-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium truncate">
                          {call.direction?.startsWith("outbound")
                            ? (call.to_number || "Unknown")
                            : (call.caller_number || "Unknown")}
                        </span>
                        <Badge variant="outline" className={`text-[9px] px-1 py-0 ${
                          call.status === "completed" ? "border-green-600 text-green-400" :
                          call.status === "no-answer" ? "border-yellow-600 text-yellow-400" :
                          call.status === "busy" ? (!call.direction?.startsWith("outbound") ? "border-blue-600 text-blue-400" : "border-orange-600 text-orange-400") :
                          call.status === "failed" || call.status === "canceled" ? "border-red-600 text-red-400" : ""
                        }`}>
                          {!call.direction?.startsWith("outbound") && call.status === "busy" ? "incoming" : call.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5 flex-wrap">
                        <span>{formatDateTime(call.received_at)}</span>
                        {parseInt(call.duration) > 0 && (
                          <span>&bull; {formatDuration(call.duration)}</span>
                        )}
                        <span className="text-slate-500">
                          {call.direction?.startsWith("outbound") ? "Outgoing" : "Incoming"}
                        </span>
                        {call.user_name && (
                          <span>&bull; <span className="text-slate-300">{call.user_name}</span></span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
